import { ApolloError } from '@apollo/client';

import {
  MarketingAttributionModel,
  MarketingChannel,
  MarketingCompare,
  MarketingCompareMode as MarketingGqlCompareMode,
  MarketingCompareUnit as MarketingGqlCompareUnit,
  MarketingDimension,
  MarketingFilter,
  MarketingGranularity,
  MarketingRowPredicate as MarketingGqlRowPredicate,
} from 'core/graphql';

import {
  IMarketingFilter,
  MarketingDimensionCode,
  MarketingGranularityCode,
} from './types';

/**
 * ТЗ 0088 — стык экранов раздела «Маркетинг» с кодогенерацией.
 *
 * Ответы запросов типы экранов (`types.ts`) принимают присваиванием — enum
 * кодогенерации (`MarketingChannel.Meta = 'META'`) совместим со строковым
 * союзом по значению, и `tsc` сверяет каждое поле. Обратно — в переменные
 * запроса — строковый союз в enum сам не превращается: это делают помощники
 * ниже, в одном месте.
 */

/** Фильтр экрана → `MarketingFilter` запроса. */
export const marketingGqlFilter = (
  filter: IMarketingFilter,
): MarketingFilter => ({
  dateFrom: filter.dateFrom,
  dateTo: filter.dateTo,
  channels: filter.channels?.length
    ? (filter.channels as MarketingChannel[])
    : null,
  familySlugs: filter.familySlugs?.length ? filter.familySlugs : null,
  offers: filter.offers?.length ? filter.offers : null,
  campaignIds: filter.campaignIds?.length ? filter.campaignIds : null,
  adsetIds: filter.adsetIds?.length ? filter.adsetIds : null,
  legalEntityId: filter.legalEntityId || null,
  model: (filter.model ?? null) as MarketingAttributionModel | null,
  // ТЗ 0103: ключ — только когда сужает (запросы без фильтра объявлений не
  // делятся на «с пустым списком» и «без него»).
  ...(filter.adIds?.length ? { adIds: filter.adIds } : {}),
});

/** Режим сравнения адреса (`cmp`) → `MarketingCompareMode` сервера. */
const GQL_COMPARE_MODE: Readonly<
  Record<'prev' | 'yoy' | 'custom', MarketingGqlCompareMode>
> = {
  prev: MarketingGqlCompareMode.Previous,
  yoy: MarketingGqlCompareMode.Year,
  custom: MarketingGqlCompareMode.Custom,
};

/** Что спросить у сервера о сравнении (вход `marketingGqlCompare`). */
export interface IMarketingCompareRequestInput {
  /** `prev` | `none` | `yoy` | `custom` — значения `cmp` адреса. */
  mode: string;
  /** Свои даты сравнения — только при `custom`. */
  from?: string | null;
  to?: string | null;
  /**
   * Единица пресета (`prev` и `yoy`): DAY | WEEK | MONTH | QUARTER | YEAR |
   * ROLLING; нет — ROLLING (окно той же длины перед / −364 дня).
   */
  unit?: string | null;
}

const GQL_COMPARE_UNITS: ReadonlyArray<string> = Object.values(
  MarketingGqlCompareUnit,
);

/**
 * Сравнение адреса → переменная `compare` запроса (ТЗ 0103 §A2). Окно
 * сравнения считает сервер (`compareInfo`), фронт называет режим и — у
 * «прошлого периода» — единицу (ревью 0103 L1); «без сравнения» и свои
 * даты без целого окна — `null` (аргумент не уходит).
 */
export const marketingGqlCompare = (
  input: IMarketingCompareRequestInput | null | undefined,
): MarketingCompare | null => {
  if (!input) return null;
  const { mode, from, to, unit } = input;
  if (mode === 'custom') {
    return from && to
      ? { mode: GQL_COMPARE_MODE.custom, dateFrom: from, dateTo: to }
      : null;
  }
  const gqlUnit =
    unit && GQL_COMPARE_UNITS.includes(unit)
      ? (unit as MarketingGqlCompareUnit)
      : MarketingGqlCompareUnit.Rolling;
  if (mode === 'prev') return { mode: GQL_COMPARE_MODE.prev, unit: gqlUnit };
  // Повторное ревью 0103 (A15): «год назад» — тоже по единице пресета
  // (месяц / квартал / год — те же даты, иначе −364 дня); без единицы
  // сервер считал бы ROLLING, и месяц сравнивался бы со сдвигом на 364 дня.
  if (mode === 'yoy') return { mode: GQL_COMPARE_MODE.yoy, unit: gqlUnit };
  return null;
};

/** Быстрые фильтры строк → `having` запроса; пусто — `null`. */
export const marketingGqlHaving = (
  predicates: ReadonlyArray<string> | null | undefined,
): MarketingGqlRowPredicate[] | null =>
  predicates && predicates.length
    ? (predicates as MarketingGqlRowPredicate[])
    : null;

export const marketingGqlDimension = (
  dimension: MarketingDimensionCode,
): MarketingDimension => dimension as MarketingDimension;

export const marketingGqlGranularity = (
  granularity: MarketingGranularityCode,
): MarketingGranularity => granularity as MarketingGranularity;

/**
 * Код отказа запроса из `extensions.code` (`window_too_long`, `bad_order`…).
 * `null` — ошибки нет или она без кода (сеть, права): экран покажет общий
 * текст.
 */
export const marketingQueryErrorCode = (
  error: ApolloError | null | undefined,
): string | null => {
  if (!error) return null;
  const coded = (error.graphQLErrors ?? []).find(
    (item) => typeof item?.extensions?.code === 'string',
  );
  return coded ? String(coded.extensions?.code) : null;
};

/** Ошибка поля из `customErrors` мутации. */
export interface IMarketingFieldError {
  field: string;
  code: string;
  message: string;
}

type RawErrors =
  | ReadonlyArray<{
      field?: string | null;
      code?: string | null;
      message?: string | null;
    } | null>
  | null
  | undefined;

export const toMarketingFieldErrors = (
  raw: RawErrors,
): IMarketingFieldError[] =>
  (raw ?? [])
    .filter((error): error is NonNullable<typeof error> => Boolean(error))
    .map((error) => ({
      field: error.field ?? '',
      code: error.code ?? '',
      message: error.message ?? '',
    }));
